/**
 * Sentry (frontend error tracking), DSN-gated.
 *
 * Call `initSentry()` once at boot (index.tsx). With no `REACT_APP_SENTRY_DSN`
 * it's a clean no-op (no SDK init, no network) — dev/unconfigured envs are
 * unaffected. `captureError()` is a safe wrapper the error boundaries call; it
 * forwards to Sentry only when initialized, otherwise no-ops.
 *
 * Env:
 *   REACT_APP_SENTRY_DSN  Sentry DSN (required to enable)
 */
import * as Sentry from "@sentry/react";

let initialized = false;

export function initSentry(): void {
  if (initialized) return;
  const dsn = process.env.REACT_APP_SENTRY_DSN;
  if (!dsn) {
    if (process.env.NODE_ENV === "development") {
      // eslint-disable-next-line no-console
      console.debug("[sentry] REACT_APP_SENTRY_DSN not set — error tracking disabled");
    }
    return;
  }
  initialized = true;
  Sentry.init({
    dsn,
    environment: process.env.NODE_ENV,
    // Errors only by default; turn up tracesSampleRate to add performance tracing.
    tracesSampleRate: 0,
    // Match the backend's PII posture (main.py send_default_pii=False + before_send).
    sendDefaultPii: false,
    beforeSend: scrubPii,
  });
}

/** Drop the query string (and fragment) from a URL — that's where tokens/emails ride. */
function stripQuery(url: string): string {
  return url.split(/[?#]/)[0];
}

/**
 * Strip obvious PII from an event before it leaves the browser (D-70): the user
 * identity, request cookies/headers, and query strings — including those in the
 * default fetch/xhr/history breadcrumbs, which otherwise carry full URLs. The
 * error, stack, and breadcrumb *messages* are kept so triage still works.
 */
export function scrubPii<T extends Sentry.ErrorEvent>(event: T): T {
  delete event.user;
  if (event.request?.url) {
    event.request.url = stripQuery(event.request.url);
  }
  if (event.request) {
    delete event.request.cookies;
    delete event.request.headers;
  }
  // Breadcrumbs (fetch/xhr/navigation) record full URLs incl. query strings, so
  // a token/email in a URL re-enters here unless we scrub it too.
  for (const crumb of event.breadcrumbs ?? []) {
    const data = crumb.data;
    if (!data) continue;
    for (const key of ["url", "to", "from"]) {
      if (typeof data[key] === "string") data[key] = stripQuery(data[key]);
    }
  }
  return event;
}

/**
 * Report an error to Sentry if initialized; no-op otherwise. Safe to call from
 * error boundaries regardless of whether Sentry is configured.
 */
export function captureError(error: unknown, context?: Record<string, unknown>): void {
  if (!initialized) return;
  Sentry.captureException(error, context ? { extra: context } : undefined);
}
